import { useCallback, useEffect, useRef, useState } from 'react';
import SearchInput from '@components/common/SearchInput';
import { toastError, toastWarning } from '@components/common/Toast/CustomToast';
import { api } from '@/api/growth/map/api';
import { api as farmlandApi } from '@/api/growth/farmland/api';
import { FarmlandFeatureList } from '@/api/growth/farmland/type';

interface FarmlandMapProps {
    gsIdx?: number;
    onFeatureSelect: (feature: SelectFeature | null, gfIdx?: number) => void;
    selectedCoords?: { lat: number; lon: number; gfIdx: number } | null;
}

export interface SelectFeature {
    address: string;
    area: number;
    lat: number;
    lng: number;
    jimok: string;
}

const FarmlandMap = ({
    gsIdx,
    onFeatureSelect,
    selectedCoords,
}: FarmlandMapProps) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<any>(null);
    const markerRef = useRef<any>(null);
    const [infoWindow, setInfoWindow] = useState<any>(null);
    const [cadastralPolygons, setCadastralPolygons] = useState<any[]>([]); // 지적도 폴리곤 관리

    const [farmlandCropMarker, setFarmlandCropMarker] = useState<any[]>([]); // 농지 작물마커 목록
    const [farmlandCadastralPolygons, setFarmlandCadastralPolygons] = useState<
        any[]
    >([]); // 농지 지적도 목록

    // const [baseCadastralPolygons, setBaseCadastralPolygons] = useState<any[]>(
    //     [],
    // ); // 배경지적도 폴리곤 관리

    // 검색 관련 상태
    const [keyword, setKeyword] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        if (selectedCoords && map) {
            const latlng = new window.naver.maps.LatLng(
                selectedCoords.lat,
                selectedCoords.lon,
            );

            map.panTo(latlng, {
                duration: 500,
                easing: 'easeOutCubic',
            });

            map.setZoom(17);

            if (infoWindow) {
                infoWindow.close();
            }

            fetchMapFeature(
                map,
                selectedCoords.lon,
                selectedCoords.lat,
                selectedCoords.gfIdx,
            );
        } else {
            if (markerRef.current) {
                markerRef.current.setMap(null);
                markerRef.current = null;
            }

            if (infoWindow) {
                infoWindow.close();
            }
            cadastralPolygons.forEach(polygon => {
                if (polygon) {
                    polygon.setMap(null);
                }
            });
        }
    }, [selectedCoords, map]);

    useEffect(() => {
        const loadNaverMapScript = () => {
            if (window.naver && window.naver.maps) {
                initializeMap();
                return;
            }

            const existingScript = document.querySelector(
                'script[src*="openapi.map.naver.com"]',
            );
            if (existingScript) {
                existingScript.remove();
            }

            const clientId = import.meta.env.VITE_NCP_API_KEY_ID;
            const script = document.createElement('script');
            script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}&submodules=geocoder,drawing,geometry`;
            script.type = 'text/javascript';
            script.async = true;

            script.onload = () => {
                console.log('NCP 네이버 지도 API 로드 완료');
                setTimeout(initializeMap, 200);
            };

            script.onerror = error => {
                console.error('NCP 네이버 지도 API 로드 실패:', error);
            };

            document.head.appendChild(script);
        };

        loadNaverMapScript();
        return () => {
            // 컴포넌트 언마운트 시 폴리곤 정리
            cadastralPolygons.forEach(polygon => {
                if (polygon) {
                    polygon.setMap(null);
                }
            });

            if (markerRef.current) {
                markerRef.current.setMap(null);
                markerRef.current = null;
            }

            if (infoWindow) {
                infoWindow.close();
            }
        };
    }, []);

    // 지도 초기화
    const initializeMap = useCallback(() => {
        if (!mapRef.current || !window.naver || !window.naver.maps) {
            console.warn('지도 초기화 조건이 충족되지 않았습니다.');
            return;
        }

        try {
            const mapOptions = {
                center: new window.naver.maps.LatLng(37.5666805, 126.9784147),
                zoom: 17,
                minZoom: 6,
                maxZoom: 21,
                mapTypeId: window.naver.maps.MapTypeId.NORMAL,
                mapDataControl: false,
                scaleControl: true,
                logoControl: true,
                mapTypeControl: true,
                mapTypeControlOptions: {
                    style: window.naver.maps.MapTypeControlStyle.BUTTON,
                    position: window.naver.maps.Position.TOP_RIGHT,
                },
                zoomControl: true,
                zoomControlOptions: {
                    style: window.naver.maps.ZoomControlStyle.LARGE,
                    position: window.naver.maps.Position.TOP_LEFT,
                },
                tileTransition: false,
                tileDuration: 0,
                tileSpare: 0,
                tileFadeEffect: false,
                useStyleMap: false,
                usePanAnimation: false,
                useZoomAnimation: false,
                enableWheelZoom: true,
                backgroundMap: false,
                overlayZoomEffect: 'none',
                useGPUAcceleration: true,
            };

            const naverMap = new window.naver.maps.Map(
                mapRef.current,
                mapOptions,
            );

            // 네이버 지적도 레이어 추가
            const cadastral = new window.naver.maps.CadastralLayer();
            cadastral.setMap(naverMap);

            setMap(naverMap);

            // 지도 클릭 이벤트 (지적도 폴리곤 조회)
            window.naver.maps.Event.addListener(naverMap, 'click', (e: any) => {
                console.log('지도 클릭:', e.coord.lng(), e.coord.lat());

                // 기존 마커가 있다면 제거
                if (markerRef.current) {
                    markerRef.current.setMap(null);
                    markerRef.current = null;
                }

                // 기존 정보창 닫기
                if (infoWindow) {
                    infoWindow.close();
                }

                // 지적도 폴리곤 조회
                fetchMapFeature(naverMap, e.coord.lng(), e.coord.lat());
            });

            // 줌 변경 이벤트
            let zoomTimeout: NodeJS.Timeout;
            window.naver.maps.Event.addListener(
                naverMap,
                'zoom_changed',
                (zoom: number) => {
                    clearTimeout(zoomTimeout);
                    zoomTimeout = setTimeout(() => {
                        console.log('줌 레벨 변경:', zoom);

                        // // 현재 보이는 영역의 바운딩 박스 좌표 출력
                        // const bounds = naverMap.getBounds();
                        // const ne = bounds.getNE();
                        // const sw = bounds.getSW();
                        //
                        // console.log('보이는 영역 좌표:', {
                        //     northEast: { lat: ne.lat(), lng: ne.lng() },
                        //     southWest: { lat: sw.lat(), lng: sw.lng() },
                        // });
                    }, 100);
                },
            );

            console.log('NCP 네이버 지도가 성공적으로 초기화되었습니다.');
            fetchFarmlandFeatureList(naverMap);
        } catch (error) {
            console.error('지도 초기화 중 오류:', error);
            toastError(
                '지도 초기화에 실패했습니다. 페이지를 새로고침해주세요.',
            );
        }
    }, []);

    const fetchFarmlandFeatureList = useCallback(
        async (currentMap: any) => {
            // gsIdx가 없으면 early return
            if (!gsIdx) return;

            farmlandApi
                .farmlandFeatureList(gsIdx, { skipLoading: true })
                .then(featureListRes => {
                    // API 응답 데이터가 있는지 확인
                    if (!featureListRes || !featureListRes.data.length) {
                        return;
                    }

                    // 기존 폴리곤 제거 - 한 번에 처리
                    if (farmlandCadastralPolygons.length > 0) {
                        farmlandCadastralPolygons.forEach(polygon => {
                            if (polygon) {
                                polygon.setMap(null);
                            }
                        });
                    }

                    const newPolygons: any[] = [];
                    // 다중폴리곤 그리기
                    featureListRes.data.forEach(item => {
                        const coordinates = item.features.coordinates;
                        const polygonPaths: any[] = [];

                        // MultiPolygon의 경우 여러 개의 폴리곤이 있을 수 있음
                        coordinates.forEach((polygon: number[][][]) => {
                            // 각 폴리곤의 외곽선 (첫 번째 배열)
                            const exteriorRing = polygon[0];
                            const path = exteriorRing.map(
                                (coord: number[]) =>
                                    new window.naver.maps.LatLng(
                                        coord[1],
                                        coord[0],
                                    ), // [경도, 위도] -> LatLng(위도, 경도)
                            );
                            polygonPaths.push(path);
                        });

                        // TODO 패턴처리
                        const fillColor = '#03fd50';
                        const strokeColor = '#007a0e';

                        // 새 폴리곤 생성 및 지도에 추가
                        const cadastralPolygon = new window.naver.maps.Polygon({
                            map: currentMap,
                            paths: polygonPaths,
                            fillColor: fillColor,
                            fillOpacity: 0.5,
                            strokeColor: strokeColor,
                            strokeOpacity: 1,
                            strokeWeight: 1,
                            strokeLineCap: 'round',
                            strokeStyle: 'dash',
                            clickable: true,
                            zIndex: 2,
                        });

                        newPolygons.push(cadastralPolygon);

                        //폴리곤 클릭 시에도 좌표 확인 가능
                        window.naver.maps.Event.addListener(
                            cadastralPolygon,
                            'click',
                            (e: {
                                coord: { lng: () => number; lat: () => number };
                            }) => {
                                console.log('폴리곤 클릭 좌표:', e.coord);

                                // TODO 수정팝업띄우기
                                // 기존 정보창 닫기
                                if (infoWindow) {
                                    infoWindow.close();
                                }

                                // 지적도 폴리곤 조회
                                fetchMapFeature(
                                    currentMap,
                                    e.coord.lng(),
                                    e.coord.lat(),
                                    item.gfIdx, // 수정팝업호출
                                );
                            },
                        );

                        // 호버이벤트
                        window.naver.maps.Event.addListener(
                            cadastralPolygon,
                            'mouseover',
                            function () {
                                cadastralPolygon.setOptions({
                                    fillOpacity: 0.5,
                                    fillColor: '#fbe702',
                                    strokeColor: '#677503',
                                });
                            },
                        );

                        window.naver.maps.Event.addListener(
                            cadastralPolygon,
                            'mouseout',
                            function () {
                                cadastralPolygon.setOptions({
                                    fillOpacity: 0.5,
                                    fillColor: fillColor,
                                    strokeColor: strokeColor,
                                });
                            },
                        );
                    });

                    setFarmlandCadastralPolygons(newPolygons);

                    addCropMarker(featureListRes, currentMap);

                    if (newPolygons.length > 0) {
                        // 등록된 농지가 있으면, 첫번째 농지위치로 이동

                        currentMap.panTo(getCenter(newPolygons[0]), {
                            duration: 500, // 애니메이션 지속 시간 (ms)
                            easing: 'easeOutCubic', // easing 함수
                        });
                    }
                });
        },
        [gsIdx, farmlandCadastralPolygons, infoWindow],
    );

    const getCenter = (polygon: any): any => {
        const bounds = new window.naver.maps.LatLngBounds();

        // getPaths() 메서드로 경로 배열 가져오기
        const paths = polygon.getPaths();

        paths.forEach((path: any) => {
            // getLength()와 getAt()으로 각 좌표 접근
            for (let i = 0; i < path.getLength(); i++) {
                bounds.extend(path.getAt(i));
            }
        });

        const centerLat = (bounds.getNE().lat() + bounds.getSW().lat()) / 2;
        const centerLng = (bounds.getNE().lng() + bounds.getSW().lng()) / 2;
        return new window.naver.maps.LatLng(centerLat, centerLng);
    };

    const CROP_IMAGE_MAP = {
        옥수수: {
            path: '/images/crop/corn.png',
        },
        감자: {
            path: '/images/crop/potato.png',
        },
        벼: {
            path: '/images/crop/rice.png',
        },
        콩: {
            path: '/images/crop/soybean.png',
        },
        잔디: {
            path: '/images/crop/bermudagrass.png',
        },
    } as const;

    const addCropMarker = (res: FarmlandFeatureList, currentMap: any) => {
        // 기존 폴리곤 제거 - 한 번에 처리
        if (farmlandCropMarker.length > 0) {
            farmlandCropMarker.forEach(marker => {
                if (marker) {
                    marker.setMap(null);
                }
            });
        }

        const newCropMarker: any[] = [];

        res.data.forEach(item => {
            if (!item.gcIdx) {
                // 작물이 선택안되어있는경우
                return;
            }

            const bbox = item.features.bbox;
            const centerLng = (bbox[0] + bbox[2]) / 2;
            const centerLat = (bbox[1] + bbox[3]) / 2;

            // 작물이미지선택
            const cropInfo =
                CROP_IMAGE_MAP[item.cropName as keyof typeof CROP_IMAGE_MAP];

            if (!cropInfo) return;

            const newMarker = new window.naver.maps.Marker({
                position: new window.naver.maps.LatLng(centerLat, centerLng),
                map: currentMap,
                title: item.cropName,
                icon: {
                    content: `
                <div class="custom-marker bounce">
                    <img src="${cropInfo.path}" width="20" height="20" />
                </div>
            `,
                    scaledSize: new window.naver.maps.Size(20, 20), // 강제 리사이즈
                    origin: new window.naver.maps.Point(0, 0),
                    anchor: new window.naver.maps.Point(10, 30),
                },
                clickable: true,
                //animation: window.naver.maps.Animation.BOUNCE,
            });
            newCropMarker.push(newMarker);
        });

        setFarmlandCropMarker(newCropMarker);
    };

    // 지적도 정보 조회 및 폴리곤 그리기
    const fetchMapFeature = useCallback(
        async (currentMap: any, lng: number, lat: number, gfIdx?: number) => {
            try {
                const featureData = await api.getMapFeature(
                    {
                        longitude: lng,
                        latitude: lat,
                    },
                    { skipLoading: true },
                );

                console.log(featureData);

                // API 응답 데이터가 있는지 확인
                if (!featureData || !featureData.coordinates) {
                    toastWarning('해당 위치의 지적도 정보를 찾을 수 없습니다.');
                    return;
                }

                // 기존 폴리곤들만 제거 (한 번만!)
                cadastralPolygons.forEach(polygon => {
                    if (polygon) {
                        polygon.setMap(null);
                    }
                });
                // 배열초기화
                cadastralPolygons.shift();
                setCadastralPolygons([]);

                // GeoJSON MultiPolygon 좌표 처리
                const coordinates = featureData.coordinates;
                const polygonPaths: any[] = [];

                // MultiPolygon의 경우 여러 개의 폴리곤이 있을 수 있음
                coordinates.forEach((polygon: number[][][]) => {
                    // 각 폴리곤의 외곽선 (첫 번째 배열)
                    const exteriorRing = polygon[0];
                    const path = exteriorRing.map(
                        (coord: number[]) =>
                            new window.naver.maps.LatLng(coord[1], coord[0]), // [경도, 위도] -> LatLng(위도, 경도)
                    );
                    polygonPaths.push(path);
                });
                const fillColor = gfIdx ? '#4babef' : '#03aeae';
                const strokeColor = gfIdx ? '#677503' : '#0d806f';
                const strokeStyle = gfIdx ? 'solid' : 'dash';

                // 새 폴리곤 생성 및 지도에 추가
                const cadastralPolygon = new window.naver.maps.Polygon({
                    map: currentMap,
                    paths: polygonPaths,
                    fillColor: fillColor,
                    fillOpacity: 0.5,
                    strokeColor: strokeColor,
                    strokeOpacity: 1,
                    strokeWeight: 1,
                    strokeStyle: strokeStyle,
                    strokeLineCap: 'round',
                    clickable: true,
                    zIndex: 3,
                });

                cadastralPolygons.push(cadastralPolygon);
                setCadastralPolygons(cadastralPolygons);

                //호버이벤트;
                window.naver.maps.Event.addListener(
                    cadastralPolygon,
                    'mouseover',
                    function () {
                        cadastralPolygon.setOptions({
                            fillOpacity: 0.7,
                        });
                    },
                );

                window.naver.maps.Event.addListener(
                    cadastralPolygon,
                    'mouseout',
                    function () {
                        cadastralPolygon.setOptions({
                            fillOpacity: 0.5,
                        });
                    },
                );

                //currentMap.setCenter(center);

                const latLng = getCenter(cadastralPolygon);

                currentMap.panTo(getCenter(cadastralPolygon), {
                    duration: 500, // 애니메이션 지속 시간 (ms)
                    easing: 'easeOutCubic', // easing 함수
                });

                // 폴리곤 클릭 이벤트 - 지적도 정보 표시
                window.naver.maps.Event.addListener(
                    cadastralPolygon,
                    'click',
                    () => {
                        addInfoWindows(currentMap, featureData, latLng, gfIdx);
                    },
                );

                addInfoWindows(currentMap, featureData, latLng, gfIdx);

                console.log('지적도 폴리곤이 성공적으로 그려졌습니다.');
            } catch (error) {
                console.error('지적도 정보 조회 중 오류:', error);
                toastError('지적도 정보를 불러오는 중 오류가 발생했습니다.');
            }
        },
        [infoWindow, cadastralPolygons],
    );

    const formatPyeong = (areaInSquareMeters: number): number => {
        if (!areaInSquareMeters || isNaN(areaInSquareMeters)) return 0;
        const result = parseFloat(String(areaInSquareMeters)) / 3.3058;
        return Number(`${result.toFixed(2)}`);
    };

    const addInfoWindows = (
        currentMap: any,
        featureData: any,
        latLng: any,
        gfIdx?: number,
    ) => {
        // 기존 정보창 닫기
        if (infoWindow) {
            infoWindow.close();
        }

        const jimok = featureData.properties.jimok;
        const area = featureData.properties.area;
        const pyeong = formatPyeong(area);

        // selectFeature 객체 생성 및 저장
        const featureObject: SelectFeature = {
            address:
                featureData.properties?.roadAddr ||
                featureData.properties?.addr ||
                '정보 없음',
            area: area || 0,
            lat: Number(latLng.lat().toFixed(6)),
            lng: Number(latLng.lng().toFixed(6)),
            jimok: jimok,
        };

        // 지적도 정보창 생성
        const cadastralInfoContent = `
            <div class="custom-infowindow">
                <button
        onclick="closeInfoWindow()"
        style="
        position: absolute;
        top: 13px;
        right: 11px;
        width: 20px;
        height: 20px;
        border: none;
        background: transparent;
        color: #666;
        font-size: 14px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        z-index: 1000;
        "
        title="닫기"
        >
        <i class="ri-close-large-line"></i>
        </button>
                <div class="text-primary fw-bold border-bottom mb-2 pb-2">
                    <i class="ri-map-pin-line"></i> 지적도 정보
                </div>
                <div class="row-item">
                    <strong>주소:</strong> ${featureData.properties?.roadAddr || featureData.properties?.addr}
                </div>
                <div class="row-item">
                    <strong>지번:</strong> ${featureData.properties?.jibun || '정보 없음'}
                </div>
                <div class="row-item">
                    <strong>PNU:</strong> ${featureData.properties?.pnu || '정보 없음'}
                </div>
                ${
                    area
                        ? `
                    <div class="row-item">
                        <strong>면적:</strong> ${area.toLocaleString()}㎡ / ${pyeong?.toLocaleString()}평
                    </div>
            `
                        : ''
                }
                <div class="row-item">
                    <strong>지목:</strong> ${jimok || '정보 없음'}
                </div>
                <div class="sub-item">
                    클릭 위치: ${latLng.lat().toFixed(6)}, ${latLng.lng().toFixed(6)}
                </div>

                <div class="mt-3">
                    ${
                        gfIdx
                            ? `<button class="btn btn-sm btn-info btn-wave w-100" onclick="modifyBtnClick()"><i class="ri-pencil-line"></i>농지수정</button>`
                            : `<button class="btn btn-sm btn-success btn-wave w-100" onclick="addBtnClick()"><i class="ri-add-line"></i>농지등록</button>`
                    }
                </div>
            </div>
        `;

        // 정보창 생성 및 표시
        const cadastralInfoWindow = new window.naver.maps.InfoWindow({
            content: cadastralInfoContent,
            maxWidth: 400,
            borderColor: '#a8a8a8',

            borderWidth: 0,
            backgroundColor: 'transparent',
            disableAnchor: true, // 꼬리표를 제거합니다.
            pixelOffset: new window.naver.maps.Point(0, -50),
        });

        const cood = new window.naver.maps.LatLng(latLng.lat(), latLng.lng());
        cadastralInfoWindow.open(currentMap, cood);
        setInfoWindow(cadastralInfoWindow);

        // 전역 함수로 닫기 기능 제공
        (window as any).closeInfoWindow = () => {
            if (cadastralInfoWindow && cadastralInfoWindow.getMap()) {
                cadastralInfoWindow.close();
            }
        };

        (window as any).addBtnClick = () => {
            if (cadastralInfoWindow && cadastralInfoWindow.getMap()) {
                cadastralInfoWindow.close();
            }
            onFeatureSelect(featureObject);
        };
        (window as any).modifyBtnClick = () => {
            if (cadastralInfoWindow && cadastralInfoWindow.getMap()) {
                cadastralInfoWindow.close();
            }
            onFeatureSelect(featureObject, gfIdx);
        };
    };

    // 주소 검색 함수
    const searchAddress = useCallback(
        async (searchKeyword: string) => {
            if (
                !searchKeyword.trim() ||
                !window.naver ||
                !window.naver.maps ||
                !map
            ) {
                if (!searchKeyword.trim()) {
                    toastWarning('검색어를 입력해주세요.');
                }
                return;
            }

            if (isSearching) {
                return; // 이미 검색 중이면 중복 실행 방지
            }

            setIsSearching(true);

            // 검색 API 호출
            searchWithAPI(searchKeyword);
        },
        [map, isSearching],
    );

    const searchWithAPI = async (searchKeyword: string) => {
        try {
            // 기존 마커가 있다면 제거
            if (markerRef.current) {
                markerRef.current.setMap(null);
                markerRef.current = null;
            }

            // 기존 정보창이 있다면 닫기
            if (infoWindow) {
                infoWindow.close();
            }

            // 주소 검색 API 호출
            const addressData = await api.searchAddress(
                { query: searchKeyword },
                { skipLoading: true },
            );

            if (!addressData) {
                toastWarning('검색 결과를 찾을 수 없습니다.');
                return;
            }

            // 좌표 생성
            const lat = parseFloat(addressData.point.y);
            const lng = parseFloat(addressData.point.x);

            if (isNaN(lat) || isNaN(lng)) {
                toastError('유효하지 않은 좌표 정보입니다.');
                return;
            }

            const latlng = new window.naver.maps.LatLng(lat, lng);

            // 지도 중심을 검색 결과 위치로 이동
            //map.setCenter(latlng);

            map.panTo(latlng, {
                duration: 500, // 애니메이션 지속 시간 (ms)
                easing: 'easeOutCubic', // easing 함수
            });

            map.setZoom(17); // 적절한 줌 레벨로 설정

            // 검색 결과 마커 추가 (기존 addMarker 메서드 사용)
            const searchResultItem = {
                roadAddress: addressData.address.road,
                jibunAddress: addressData.address.parcel,
                address: addressData.address.road || addressData.address.parcel,
            };

            addMarker(latlng, searchResultItem);

            console.log('주소 검색 완료:', searchKeyword, addressData);
        } catch (error) {
            console.error('주소 검색 중 오류 발생:', error);
            toastError('주소 검색 중 오류가 발생했습니다.');
        } finally {
            setIsSearching(false);
        }
    };

    // 검색 핸들러
    const handleSearch = useCallback(
        (value: string) => {
            setKeyword(value);
            if (value.trim()) {
                searchAddress(value);
            }
        },
        [searchAddress],
    );

    const addMarker = (latlng: any, item: any) => {
        // 주소 정보 추출
        const roadAddress = item.roadAddress || item.address || '';
        const jibunAddress = item.jibunAddress || '';
        const displayTitle = roadAddress || jibunAddress;

        const newMarker = new window.naver.maps.Marker({
            position: latlng,
            map: map,
            title: displayTitle,
            icon: {
                url: '/images/map/marker_v3.png',
                scaledSize: new window.naver.maps.Size(46, 46), // 강제 리사이즈
                origin: new window.naver.maps.Point(0, 0),
                anchor: new window.naver.maps.Point(23, 46),
            },
            clickable: true,
        });

        markerRef.current = newMarker; // 즉시 반영

        // 정보창 내용 생성
        const infoContent = `
            <div class="custom-infowindow">
                <!-- 닫기 버튼 -->
                <button
                    onclick="closeInfoWindow()"
                    style="
                        position: absolute;
                        top: 13px;
                        right: 11px;
                        width: 20px;
                        height: 20px;
                        border: none;
                        background: transparent;
                        color: #666;
                        font-size: 14px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        line-height: 1;
                        z-index: 1000;
                    "
                    title="닫기"
                >
                    <i class="ri-close-large-line"></i>
                </button>
                <div class="text-primary fw-bold border-bottom mb-2 pb-2">
                    <i class="ri-map-pin-line"></i> 검색 결과
                </div>
                ${
                    roadAddress
                        ? `
                    <div class="row-item">
                        <strong>도로명:</strong> ${roadAddress}
                    </div>
                `
                        : ''
                }
                ${
                    jibunAddress && jibunAddress !== roadAddress
                        ? `
                    <div class="row-item">
                        <strong>지번:</strong> ${jibunAddress}
                    </div>
                `
                        : ''
                }
                <div class="sub-item">
                    위도: ${latlng.lat().toFixed(6)}, 경도: ${latlng.lng().toFixed(6)}
                </div>
            </div>
        `;

        // 정보창 생성 및 표시
        const newInfoWindow = new window.naver.maps.InfoWindow({
            content: infoContent,
            maxWidth: 350,
            borderColor: '#a8a8a8',

            borderWidth: 0,
            backgroundColor: 'transparent',
            disableAnchor: true, // 꼬리표를 제거합니다.
            pixelOffset: new window.naver.maps.Point(0, -20),
        });

        newInfoWindow.open(map, newMarker);
        setInfoWindow(newInfoWindow);

        // 마커 클릭 이벤트
        window.naver.maps.Event.addListener(newMarker, 'click', () => {
            if (newInfoWindow.getMap()) {
                newInfoWindow.close();
            } else {
                newInfoWindow.open(map, newMarker);
            }
        });

        // 전역 함수로 닫기 기능 제공
        (window as any).closeInfoWindow = () => {
            if (newInfoWindow && newInfoWindow.getMap()) {
                newInfoWindow.close();
            }
        };
    };

    return (
        <div className="row">
            <div className="col-xl-12">
                <div className="card custom-card">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-12">
                                <SearchInput
                                    placeholder="주소나 장소명을 입력해주세요."
                                    value={keyword || ''}
                                    onSearch={handleSearch}
                                />
                                <div className="text-muted fs-11 mt-3">
                                    주소를 검색하거나, 지도에서 농지를 선택후
                                    농지등록을 진행하세요.
                                </div>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-12">
                                <div
                                    ref={mapRef}
                                    style={{ minHeight: '725px' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FarmlandMap;
